// Response
import {Catalog} from '../../../../common/types/common';
import * as GlobalConstants from '../../../constants/GlobalConstants';
import {DocumentGroupDTO} from '../../../../admin/loan/common/types/epics';

export interface LinkedMessage {
    [key: string]: string;
}

export interface Notification {
    errorMessages: string[];
    warningMessages: string[];
    successMessages: string[];
    linkedMessages: LinkedMessage[];
}

export interface BaseResponse<T> {
    data: T;
    notifications: Notification;
    page: number;
    size: number;
    status: number;
    total_items: number;
    total_pages: number;
}

export class CalculatorApplicantSummaryDTO {
    downPayment: DisplaySummaryDTO;
    includeInsurance: boolean;
    state: Catalog;
    term: number;

    constructor(downPayment: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                includeInsurance: boolean = false,
                state: Catalog = GlobalConstants.EMPTY_CATALOG,
                term: number = GlobalConstants.EMPTY_NUMBER) {
        this.downPayment = downPayment;
        this.includeInsurance = includeInsurance;
        this.state = state;
        this.term = term;
    }
}

export class HomeApplicantSummaryDTO {
    address: string;
    city: Catalog;
    colony: Catalog;
    extNumber: string;
    intNumber: string;
    colonies: Catalog[];
    municipality: Catalog;
    phone: string;
    postalCode: string;
    state: Catalog;
    houseHoldMeasure: Catalog;
    houseHoldTime: number;
    houseType: CatalogSummaryDTO;

    constructor(address: string = GlobalConstants.EMPTY_STRING,
                city: Catalog = GlobalConstants.EMPTY_CATALOG,
                colony: Catalog = GlobalConstants.EMPTY_CATALOG,
                extNumber: string = GlobalConstants.EMPTY_STRING,
                intNumber: string = GlobalConstants.EMPTY_STRING,
                municipality: Catalog = GlobalConstants.EMPTY_CATALOG,
                phone: string = GlobalConstants.EMPTY_STRING,
                postalCode: string = GlobalConstants.EMPTY_STRING,
                state: Catalog = GlobalConstants.EMPTY_CATALOG,
                houseHoldMeasure: Catalog = GlobalConstants.EMPTY_CATALOG,
                houseHoldTime: number = GlobalConstants.EMPTY_NUMBER,
                houseType = GlobalConstants.EMPTY_CATALOG,
    ) {
        this.address = address;
        this.city = city;
        this.colony = colony;
        this.extNumber = extNumber;
        this.intNumber = intNumber;
        this.municipality = municipality;
        this.phone = phone;
        this.postalCode = postalCode;
        this.state = state;
        this.houseHoldMeasure = houseHoldMeasure;
        this.houseHoldTime = houseHoldTime;
        this.houseType = houseType;
    }
}

export interface LoanApplicationApplicantSummaryDTO {
    finished: boolean;
    acceptTerms: boolean;
    bureauNipValidated: boolean;
    bureauEligible: boolean;
    acceptQuote: string;
    termsAndConditionsNipValidated: boolean;
    hasBureauError: boolean;
    bureauOptionSaved: boolean;
    validationSkipped: boolean;
    token: string;
    updatedAt: number;
    calculator: CalculatorApplicantSummaryDTO;
    desiredVehicle: DesiredVehicleApplicantSummaryDTO;
    insurance: InsuranceApplicantSummaryDTO;
    personal: PersonalApplicantSummaryDTO;
    home: HomeApplicantSummaryDTO;
    employmentData: WorkApplicantSummaryDTO;
    summary: SummaryApplicantSummaryDTO;
    marketingSource: Catalog;
}

export interface LoanApplicationTurboCashSummaryDTO {
    continueHeader: ContinueHeaderSummaryDTO;
    applicantInfo : ApplicantInfoSummaryDTO;
    employmentInfoDTO : EmploymentInfoSummaryDTO;
    references : [ReferencesSummaryDTO];
    documents: [DocumentsSummaryDTO];
}

export interface DisplaySummaryDTO {
    value: number;
    displayValue: string;
}

export interface CatalogSummaryDTO {
    id: string;
    value: string;
}

export interface InsuranceApplicantSummaryDTO {
    includeInsurance: boolean;
    bureauOption: Catalog[];
}

export interface InvoiceInfoDTO {
    billingTypeId: string;
    taxRegimeId?: string;
    billingDocumentId?: string;
}

export class PersonalApplicantSummaryDTO {
    birthDate: string;
    cellPhone: string;
    docNumber: string;
    email: string;
    firstName: string;
    lastName: string;
    secondLastName: string;
    secondName: string;
    nationality: Catalog;
    gender: Catalog;
    civilStatus: Catalog;
    studiesLevel: Catalog;
    economicDependents: Catalog;
    alreadyHasVehicle: boolean;

    constructor(birthDate: string = GlobalConstants.EMPTY_STRING,
                cellPhone: string = GlobalConstants.EMPTY_STRING,
                docNumber: string = GlobalConstants.EMPTY_STRING,
                email: string = GlobalConstants.EMPTY_STRING,
                firstName: string = GlobalConstants.EMPTY_STRING,
                lastName: string = GlobalConstants.EMPTY_STRING,
                secondLastName: string = GlobalConstants.EMPTY_STRING,
                secondName: string = GlobalConstants.EMPTY_STRING,
                nationality = GlobalConstants.EMPTY_CATALOG,
                gender = GlobalConstants.EMPTY_CATALOG,
                civilStatus = GlobalConstants.EMPTY_CATALOG,
                studiesLevel = GlobalConstants.EMPTY_CATALOG,
                economicDependents = GlobalConstants.EMPTY_CATALOG,
                alreadyHasVehicle = false,
    ) {
        this.birthDate = birthDate;
        this.cellPhone = cellPhone;
        this.docNumber = docNumber;
        this.email = email;
        this.firstName = firstName;
        this.lastName = lastName;
        this.secondLastName = secondLastName;
        this.secondName = secondName;
        this.nationality = nationality;
        this.gender = gender;
        this.civilStatus = civilStatus;
        this.studiesLevel = studiesLevel;
        this.economicDependents = economicDependents;
        this.alreadyHasVehicle = alreadyHasVehicle;
    }
}

export class ContinueHeaderSummaryDTO {
    completeName: string;
    firstName: string;
    lastName: string;
    secondName: string;
    secondLastName: string;
    vehiclePrice: DisplaySummaryDTO;
    financingDownPayment: DisplaySummaryDTO;
    financingTerm: string;
    monthlyPayment: DisplaySummaryDTO;
    financingAmount: DisplaySummaryDTO;

    constructor(completeName: string = GlobalConstants.EMPTY_STRING,
                firstName: string = GlobalConstants.EMPTY_STRING,
                lastName: string = GlobalConstants.EMPTY_STRING,
                secondName: string = GlobalConstants.EMPTY_STRING,
                secondLastName: string = GlobalConstants.EMPTY_STRING,
                vehiclePrice: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                financingDownPayment: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                financingTerm: string = GlobalConstants.EMPTY_STRING,
                monthlyPayment: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                financingAmount: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE) {
        this.completeName = completeName;
        this.firstName = firstName;
        this.lastName = lastName;
        this.secondName = secondName;
        this.secondLastName = secondLastName;
        this.vehiclePrice = vehiclePrice;
        this.financingDownPayment = financingDownPayment;
        this.financingTerm = financingTerm;
        this.monthlyPayment = monthlyPayment;
        this.financingAmount = financingAmount;
    }
}

export class ApplicantInfoSummaryDTO {
    civilStatus: CatalogSummaryDTO;
    regimen: CatalogSummaryDTO;
    roleAtHome: CatalogSummaryDTO;
    studiesLevel: CatalogSummaryDTO;
    economicDependents: string;
    curp: string;
    houseType: CatalogSummaryDTO;
    holdTime: string;
    holdTimeMeasure: string;

    constructor(economicDependents: string = GlobalConstants.EMPTY_STRING,
                curp: string = GlobalConstants.EMPTY_STRING,
                holdTime: string = GlobalConstants.EMPTY_STRING,
                holdTimeMeasure: string = GlobalConstants.EMPTY_STRING,
                civilStatus: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                regimen: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                roleAtHome: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                studiesLevel: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                houseType: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG) {
        this.civilStatus = civilStatus;
        this.regimen = regimen;
        this.roleAtHome = roleAtHome;
        this.studiesLevel = studiesLevel;
        this.economicDependents = economicDependents;
        this.curp = curp;
        this.houseType = houseType;
        this.holdTime = holdTime;
        this.holdTimeMeasure = holdTimeMeasure;
    }
}

export class EmploymentInfoSummaryDTO {
    workplaceName: string;
    position: string;
    workingSince: DisplaySummaryDTO;
    employmentBussinessType: CatalogSummaryDTO;
    address: string;
    extNumber: string;
    intNumber: string;
    streets: string;
    postalCode: string;
    state: CatalogSummaryDTO;
    city: CatalogSummaryDTO;
    municipality: CatalogSummaryDTO;
    colony: CatalogSummaryDTO;
    workplacePhone: string;
    workplacePhoneExt: string;
    monthlyIncomes: DisplaySummaryDTO;

    constructor(workplaceName: string = GlobalConstants.EMPTY_STRING,
                position: string = GlobalConstants.EMPTY_STRING,
                workingSince: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                employmentBussinessType: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                address: string = GlobalConstants.EMPTY_STRING,
                extNumber: string = GlobalConstants.EMPTY_STRING,
                intNumber: string = GlobalConstants.EMPTY_STRING,
                streets: string = GlobalConstants.EMPTY_STRING,
                postalCode: string = GlobalConstants.EMPTY_STRING,
                state: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                city: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                municipality: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                colony: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                workplacePhone: string = GlobalConstants.EMPTY_STRING,
                workplacePhoneExt: string = GlobalConstants.EMPTY_STRING,
                monthlyIncomes: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE) {
        this.workplaceName = workplaceName;
        this.position = position;
        this.workingSince = workingSince;
        this.employmentBussinessType = employmentBussinessType;
        this.address = address;
        this.extNumber = extNumber;
        this.intNumber = intNumber;
        this.streets = streets;
        this.postalCode = postalCode;
        this.state = state;
        this.city = city;
        this.municipality = municipality;
        this.colony = colony;
        this.workplacePhone = workplacePhone;
        this.workplacePhoneExt = workplacePhoneExt;
        this.monthlyIncomes = monthlyIncomes;
    }
}

export class ReferencesSummaryDTO {
    completeName: string;
    phone: string;
    landLinePhone: string;
    address: string;
    extNumber: string;
    intNumber: string;
    postalCode: string;
    state: CatalogSummaryDTO;
    city: CatalogSummaryDTO;
    municipality: CatalogSummaryDTO;
    colony: CatalogSummaryDTO;
    colonies: [CatalogSummaryDTO];

    constructor(completeName: string = GlobalConstants.EMPTY_STRING,
                phone: string = GlobalConstants.EMPTY_STRING,
                landLinePhone: string = GlobalConstants.EMPTY_STRING,
                address: string = GlobalConstants.EMPTY_STRING,
                extNumber: string = GlobalConstants.EMPTY_STRING,
                intNumber: string = GlobalConstants.EMPTY_STRING,
                postalCode: string = GlobalConstants.EMPTY_STRING,
                state: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                city: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                municipality: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                colony: CatalogSummaryDTO = GlobalConstants.EMPTY_CATALOG,
                colonies: [CatalogSummaryDTO] = [GlobalConstants.EMPTY_CATALOG]) {
        this.completeName = completeName;
        this.phone = phone;
        this.landLinePhone = landLinePhone;
        this.address = address;
        this.extNumber = extNumber;
        this.intNumber = intNumber;
        this.postalCode = postalCode;
        this.state = state;
        this.city = city;
        this.municipality = municipality;
        this.colony = colony;
        this.colonies = colonies;
    }
}

export class DocumentsSummaryDTO {
    classification: string;
    classificationId: string;
    documentGroups: [DocumentGroupDTO];
}

export interface SummaryApplicantSummaryDTO {
    commissionAmount: DisplaySummaryDTO;
    downPayment: DisplaySummaryDTO;
    financingAmount: DisplaySummaryDTO;
    insuranceAmount: DisplaySummaryDTO;
    monthlyAmount: DisplaySummaryDTO;
    price: DisplaySummaryDTO;
    term: number;
}

export class DesiredVehicleApplicantSummaryDTO {
    price: DisplaySummaryDTO;
    brand: Catalog;
    model: Catalog;
    version: Catalog;
    year: number;

    constructor(price: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                brand: Catalog = GlobalConstants.EMPTY_CATALOG,
                model: Catalog = GlobalConstants.EMPTY_CATALOG,
                version: Catalog = GlobalConstants.EMPTY_CATALOG,
                year: number = GlobalConstants.EMPTY_NUMBER) {
        this.price = price;
        this.brand = brand;
        this.model = model;
        this.version = version;
        this.year = year;
    }
}

export class WorkApplicantSummaryDTO {
    workplaceName: string;
    position: string;
    workingSince: string;
    address: string;
    extNumber: string;
    intNumber: string;
    postalCode: string;
    state: Catalog;
    city: Catalog;
    municipality: Catalog;
    colonies: Catalog[];
    colony: Catalog;
    workplacePhone: string;
    workplacePhoneExt: string;
    includeMonthlyIncomes: boolean;
    monthlyIncomes: DisplaySummaryDTO;
    employmentRelationshipId: string;
    employmentBusinessType: Catalog;
    incomeProof: Catalog;
    invoiceInfo: InvoiceInfoDTO;

    constructor(workplaceName: string = GlobalConstants.EMPTY_STRING,
                position: string = GlobalConstants.EMPTY_STRING,
                workingSince: string = GlobalConstants.EMPTY_STRING,
                address: string = GlobalConstants.EMPTY_STRING,
                extNumber: string = GlobalConstants.EMPTY_STRING,
                intNumber: string = GlobalConstants.EMPTY_STRING,
                postalCode: string = GlobalConstants.EMPTY_STRING,
                state: Catalog = GlobalConstants.EMPTY_CATALOG,
                city: Catalog = GlobalConstants.EMPTY_CATALOG,
                municipality: Catalog = GlobalConstants.EMPTY_CATALOG,
                colony: Catalog = GlobalConstants.EMPTY_CATALOG,
                colonies: Catalog[] = [],
                workplacePhone: string = GlobalConstants.EMPTY_STRING,
                workplacePhoneExt: string = GlobalConstants.EMPTY_STRING,
                includeMonthlyIncomes: boolean = true,
                monthlyIncomes: DisplaySummaryDTO = GlobalConstants.EMPTY_DISPLAY_VALUE,
                employmentRelationshipId = GlobalConstants.EMPTY_STRING,
                employmentBusinessType: Catalog = GlobalConstants.EMPTY_CATALOG,
                incomeProof: Catalog = GlobalConstants.EMPTY_CATALOG,
                invoiceInfo: InvoiceInfoDTO = null,
    ) {
        this.workplaceName = workplaceName;
        this.position = position;
        this.workingSince = workingSince;
        this.address = address;
        this.extNumber = extNumber;
        this.intNumber = intNumber;
        this.postalCode = postalCode;
        this.state = state;
        this.city = city;
        this.municipality = municipality;
        this.colony = colony;
        this.colonies = colonies;
        this.workplacePhone = workplacePhone;
        this.workplacePhoneExt = workplacePhoneExt;
        this.includeMonthlyIncomes = includeMonthlyIncomes;
        this.monthlyIncomes = monthlyIncomes;
        this.employmentRelationshipId = employmentRelationshipId;
        this.employmentBusinessType = employmentBusinessType;
        this.incomeProof = incomeProof;
        this.invoiceInfo = invoiceInfo;

    }
}

export class FieldApplicantSummary<T, FE> {
    data: T;
    fieldErrors: FE;

    constructor(data: T, fieldErrors: FE) {
        this.data = data;
        this.fieldErrors = fieldErrors;
    }
}

export class Cookie {
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmTerm: string;
    utmContent: string;
    aUtmUrl: string;
    gclid: string;
    firstReferer: string;
}
